import { useState, useEffect, useRef, useMemo } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslationApp } from "../../lib/i18next";
import { InputAdornment, Tooltip } from "@mui/material";
import { ArrowRight } from "lucide-react";
import Loader from "../Loaders/Loader";
import { BUTTON_SEND, FIELD_MAX_LENGTH } from "../../i18n/keysTranslations";
import TextField from "../Form/TextField";
import { useForm } from "react-hook-form";
import useTypewriterPlaceholder from "../../hooks/useTypewriterPlaceholder";

const EMPTY_PLACEHOLDER_EXAMPLES = [];
/** Same cap the agent endpoint enforces on each user message. */
const AGENT_USER_MESSAGE_MAX_CHARS = 4000;

// White send disc in every state. Input text must not switch it to the dark fill.
// Dark arrow so the glyph stays visible on the white disc.
const ICON_SEND_BUTTON_SX = {
  width: 28,
  height: 28,
  color: "text.primary",
  backgroundColor: "common.white",
  border: "1px solid",
  borderColor: "grey.300",
  boxSizing: "border-box",
  "&:hover": {
    color: "text.primary",
    backgroundColor: "common.white",
    borderColor: "grey.300",
  },
  "&.Mui-focusVisible": {
    color: "text.primary",
    backgroundColor: "common.white",
  },
  "&.Mui-disabled": {
    color: "text.primary",
    backgroundColor: "common.white",
    borderColor: "grey.300",
    opacity: 1,
  },
};

const CommonChatMessageInput = ({
  onSendMessage,
  disabled = false,
  placeholderInput,
  placeholderExamples = EMPTY_PLACEHOLDER_EXAMPLES,
  minRows = 1,
  defaultQuestion = "",
  sx,
  multiline = true,
  messageVariant,
  iconSendButton = false,
  renderInputFrame,
}) => {
  const [error, setError] = useState(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const isSendingRef = useRef(false);
  const [file, setFile] = useState(null);
  const [showAlertUsage, setShowAlertUsage] = useState(true);
  const [hasSetDefaultQuestion, setHasSetDefaultQuestion] = useState(false);

  const { t } = useTranslationApp();

  // React Hook Form setup
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      message: "",
    },
  });

  const message = watch("message");
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)"
  );
  const placeholderPhrases = useMemo(
    () => placeholderExamples.map((key) => t(key)),
    [placeholderExamples, t]
  );
  const isPlaceholderAnimated =
    placeholderPhrases.length > 0 &&
    !prefersReducedMotion &&
    !disabled &&
    !isInputFocused;
  const typedPlaceholder = useTypewriterPlaceholder({
    phrases: placeholderPhrases,
    isPaused: !isPlaceholderAnimated || message !== "",
  });
  const placeholder = isPlaceholderAnimated
    ? typedPlaceholder
    : t(placeholderInput);

  // Set default question only once when it is received
  useEffect(() => {
    if (defaultQuestion && !hasSetDefaultQuestion) {
      setValue("message", defaultQuestion);
      setHasSetDefaultQuestion(true);
    }
  }, [defaultQuestion, hasSetDefaultQuestion, setValue]);

  useEffect(() => {
    if (message?.length > AGENT_USER_MESSAGE_MAX_CHARS) {
      setError(t(FIELD_MAX_LENGTH, { value: AGENT_USER_MESSAGE_MAX_CHARS }));
      return;
    }

    if (error) {
      setError(null);
    }
  }, [message, error, t]);

  const onClickSendMessage = async () => {
    if (isSendingRef.current || disabled) {
      return false;
    }
    if (message.length > AGENT_USER_MESSAGE_MAX_CHARS) {
      setError(t(FIELD_MAX_LENGTH, { value: AGENT_USER_MESSAGE_MAX_CHARS }));
      return false;
    }
    if (!message && file === null) {
      return false;
    }

    isSendingRef.current = true;
    setIsSending(true);
    try {
      if (showAlertUsage) {
        setShowAlertUsage(false);
      }

      const messageFormatted = message.replace("&nbsp;", " ").trim();
      const currentFile = file;

      const response = await onSendMessage(messageFormatted, currentFile);
      if (response) {
        setValue("message", "");
        setFile(null);
      }
      return response;
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  };

  const getCustomSxByVariant = () => {
    if (messageVariant === "inline") {
      return {
        p: "0px !important",
      };
    }
    return {};
  };

  const isSendDisabled = (message === "" && file === null) || disabled;

  const renderSendControl = () => {
    if (isSending) {
      return <Loader hasMarginTop={false} size={18} />;
    }

    if (iconSendButton) {
      return (
        <Tooltip title={t(BUTTON_SEND)}>
          <span>
            <IconButton
              type="button"
              size="small"
              aria-label={t(BUTTON_SEND)}
              disabled={isSendDisabled}
              onClick={onClickSendMessage}
              sx={ICON_SEND_BUTTON_SX}
            >
              <ArrowRight size={16} strokeWidth={2.5} />
            </IconButton>
          </span>
        </Tooltip>
      );
    }

    return (
      <Typography
        component="button"
        type="button"
        variant="body2"
        onClick={() => {
          if (isSendDisabled) {
            return;
          }
          onClickSendMessage();
        }}
        disabled={isSendDisabled}
        sx={{
          fontWeight: 700,
          border: "none",
          background: "none",
          padding: 0,
          cursor: isSendDisabled ? "default" : "pointer",
          color: isSendDisabled ? "text.disabled" : "text.primary",
          "&:hover": {
            color: isSendDisabled ? "text.disabled" : "text.primary",
          },
        }}
      >
        {t(BUTTON_SEND)}
      </Typography>
    );
  };

  const messageField = (
    <TextField
      control={control}
      name="message"
      disabled={disabled}
      placeholder={placeholder}
      inputProps={{
        "aria-label": t(placeholderInput),
        maxLength: AGENT_USER_MESSAGE_MAX_CHARS,
      }}
      onFocus={() => setIsInputFocused(true)}
      actionsBlur={() => setIsInputFocused(false)}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      maxRows={multiline ? 6 : undefined}
      fullWidth
      variant="filled"
      rules={{}}
      errors={errors}
      sx={{
        ...sx,
        ...getCustomSxByVariant(),
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{
              alignSelf: iconSendButton ? "flex-end" : "center",
              alignItems: "center",
              ...(iconSendButton && { mb: 0.5 }),
            }}
          >
            {renderSendControl()}
          </InputAdornment>
        ),
      }}
    />
  );
  const framedMessageField = renderInputFrame
    ? renderInputFrame(messageField)
    : messageField;

  return (
    <Box
      sx={{
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }}
      onKeyDown={(e) => {
        if ((e.code === "Enter" || e.code === "NumpadEnter") && !e.shiftKey) {
          e.preventDefault();
          if (e.repeat) {
            return;
          }
          if (e.nativeEvent?.isComposing || e.keyCode === 229) {
            return;
          }
          if (isSendDisabled || isSendingRef.current) {
            return;
          }
          onClickSendMessage();
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        {framedMessageField}
      </Box>
      {error && (
        <Typography variant="caption" sx={{ color: "error.main", mt: 1 }}>
          {error}
        </Typography>
      )}
      {file && (
        <Box
          sx={{
            px: "22px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pt: "2px",
            pb: "-2px",
            mb: 1,
          }}
        >
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              variant="body2"
              align="center"
              sx={{ mb: "2px", color: "text.primary" }}
            >
              {file.name}
            </Typography>
          </Box>
          <IconButton sx={{ p: 0 }} onClick={() => setFile(null)}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default CommonChatMessageInput;
